/**
 * A stored page changed: the change stream of page, fanned out by
 * page name (FEATURE-20261014-page-chat-hardening). A page saved from the
 * chat, the MCP tools or the editor reloads in every tab that shows it, not
 * only the tab whose drawer sent the turn.
 *
 * One subscription per client for the whole table: the client keys a
 * subscription by its tables and has no unsubscribe, so one shared stream
 * with per-page listeners is the shape that neither leaks nor doubles up.
 */
import GlobalClient from "../hermesclient/client";

const listeners = new Map<string, Set<() => void>>();
let subscribed = false;
const conversationListeners = new Map<string, Set<() => void>>();
let conversationSubscribed = false;

/** Call fn whenever the stored page of this name changes; returns the unlisten. */
export function onStoredPageChange(name: string, fn: () => void): () => void {
  if (!subscribed) {
    subscribed = true;
    GlobalClient.get().cdcSubscribe<Record<string, unknown>>(
      { tables: [{ database: "continuum", table: "page" }], startSeq: "new" },
      (_event, row) => {
        const changed = String(row?.["name"] ?? "");
        listeners.get(changed)?.forEach((f) => f());
      },
    );
  }
  let set = listeners.get(name);
  if (!set) {
    set = new Set();
    listeners.set(name, set);
  }
  set.add(fn);
  return () => {
    set.delete(fn);
  };
}

/**
 * Call fn whenever a message lands in this conversation: the tool rows and the
 * reply of a turn started with startTurn, as they are written
 * (FEATURE-20261014-page-chat-hardening, stage 2). Returns the unlisten.
 */
export function onConversationChange(conversationUid: string, fn: () => void): () => void {
  if (!conversationSubscribed) {
    conversationSubscribed = true;
    GlobalClient.get().cdcSubscribe<Record<string, unknown>>(
      { tables: [{ database: "continuum", table: "vfs_chat_message" }], startSeq: "new" },
      (_event, row) => {
        const changed = String(row?.["conversation_uid"] ?? "");
        conversationListeners.get(changed)?.forEach((f) => f());
      },
    );
  }
  let set = conversationListeners.get(conversationUid);
  if (!set) {
    set = new Set();
    conversationListeners.set(conversationUid, set);
  }
  set.add(fn);
  return () => {
    set.delete(fn);
  };
}
